import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ApiKeyManager = ({ apiKeys, onGenerateKey, onRevokeKey, onUpdateKey }) => {
  const [showKeys, setShowKeys] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');

  const toggleKeyVisibility = (keyId) => {
    setShowKeys(prev => ({
      ...prev,
      [keyId]: !prev?.[keyId]
    }));
  };

  const maskKey = (key) => {
    if (!key) return '';
    return `${key?.substring(0, 8)}${'*'?.repeat(24)}${key?.substring(key?.length - 4)}`;
  };

  const handleGenerateKey = () => {
    if (newKeyName?.trim()) {
      onGenerateKey(newKeyName?.trim());
      setNewKeyName('');
    }
  };

  const getKeyStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success';
      case 'expired':
        return 'text-destructive';
      case 'revoked':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-card-foreground">API Key Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your API keys for secure integration access
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={20} className="text-primary" />
          <span className="text-sm font-medium text-primary">Encrypted</span>
        </div>
      </div>
      {/* Generate New Key */}
      <div className="bg-muted rounded-lg p-4 mb-6">
        <h3 className="font-medium text-card-foreground mb-3">Generate New API Key</h3>
        <div className="flex items-end space-x-3">
          <div className="flex-1">
            <Input
              label="Key Name"
              placeholder="e.g., Production Dashboard, Mobile App"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e?.target?.value)}
            />
          </div>
          <Button
            variant="default"
            onClick={handleGenerateKey}
            disabled={!newKeyName?.trim()}
            iconName="Plus"
            iconPosition="left"
          >
            Generate Key
          </Button>
        </div>
      </div>
      {/* API Keys List */}
      <div className="space-y-4">
        {apiKeys?.map((key) => (
          <div
            key={key?.id}
            className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors duration-200"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-medium text-card-foreground">{key?.name}</h4>
                  <span className={`text-xs px-2 py-1 rounded-full bg-muted ${getKeyStatusColor(key?.status)}`}>
                    {key?.status}
                  </span>
                </div>
                
                <div className="flex items-center space-x-3 mb-3">
                  <div className="flex items-center space-x-2 font-mono text-sm bg-background border border-border rounded px-3 py-2 flex-1 max-w-md">
                    <span className="text-muted-foreground">
                      {showKeys?.[key?.id] ? key?.value : maskKey(key?.value)}
                    </span>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => toggleKeyVisibility(key?.id)}
                      iconName={showKeys?.[key?.id] ? "EyeOff" : "Eye"}
                    />
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => navigator.clipboard?.writeText(key?.value)}
                      iconName="Copy"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="font-medium text-card-foreground">
                      {formatDate(key?.createdAt)}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Used:</span>
                    <p className="font-medium text-card-foreground">
                      {key?.lastUsed ? formatDate(key?.lastUsed) : 'Never'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Requests:</span>
                    <p className="font-medium text-card-foreground">
                      {key?.requestCount?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Expires:</span>
                    <p className="font-medium text-card-foreground">
                      {key?.expiresAt ? formatDate(key?.expiresAt) : 'Never'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingKey(key?.id)}
                  iconName="Edit"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRevokeKey(key?.id)}
                  iconName="Trash2"
                  className="text-destructive hover:text-destructive"
                />
              </div>
            </div>

            {/* Usage Statistics */}
            {key?.status === 'active' && key?.requestCount > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Usage this month:</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((key?.requestCount / 10000) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="font-medium text-card-foreground">
                      {((key?.requestCount / 10000) * 100)?.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {apiKeys?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Key" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="font-medium text-card-foreground mb-2">No API Keys</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Generate your first API key to start integrating with external services
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeyManager;